export enum KidStatus {
  EDITING = 'editing',
  EDITED = 'edited',
  REVIEWED = 'reviewed',
  PUBLISHED = 'published',
  REJECTED = 'rejected',
}

/** Template coverage/setup state. Distinct from {@link KidStatus} on KID documents. */
export enum KidTemplateSetupStatus {
  INCOMPLETE = 'incomplete',
  COMPLETE = 'complete',
  INVALID = 'invalid',
}

/** Base for KID document operations: client, fund, share class, language */
export type KidDocumentIdentifierPayload = {
  client_id: string;
  fund_id: string;
  share_class_id: string;
  document_language: string;
};

export type KidOverviewDataItem = KidDocumentIdentifierPayload & {
  version_number: string;
  kid_id: string;
  kid_id_create_date: string;
  last_updating_datetime: string;
  last_updated_by: string;
  last_updated_by_name: string;
  document_type: string;
  kid_is_active: boolean;
  kid_figure_calculation_date: string;
  description: string;
  status: KidStatus;
};

export type KidTemplateAffectedShareClass = KidOverviewDataItem & {
  fund_name_long: string;
  /** Short fund name; shown as Fund ID in overview-style tables. */
  fund_name?: string;
  fund_name_full?: string;
  share_class_name: string;
  /** Prefer for UI (same key as share-class / overview models). */
  share_class_name_full?: string;
  /** Some template endpoints use `_long` (parallel to `fund_name_long`). */
  share_class_name_long?: string;
  share_class_isin?: string;
};

export type KidTemplateAffectedShareClassesResponse =
  KidTemplateAffectedShareClass[];

/**
 * Template payload on each row of `GET .../incomplete_documents`
 * (full template metadata as returned by the API).
 */
export type KidTemplateIncompleteEntryTemplate = KidTemplateModel & {
  template_updating_datetime?: string;
  document_language?: string;
  template_is_active?: boolean;
  template_setup_status?: KidTemplateSetupStatus | string;
  template_last_updated_by?: string;
  template_last_updated_by_name?: string | null;
  template_rendered_content?: string | null;
  existing_markers?: KidMarker[] | null;
};

/**
 * One template that is absent from one or more KID documents.
 * `missing_overviews` / `invalid_overviews` list affected documents (share classes).
 */
export type KidTemplateIncompleteEntry = {
  template_id: string;
  template: KidTemplateIncompleteEntryTemplate;
  invalid_overviews: KidTemplateAffectedShareClass[];
  missing_overviews: KidTemplateAffectedShareClass[];
};

export type KidTemplateIncompleteDocumentsResponse =
  KidTemplateIncompleteEntry[];

/** Nested `template` on {@link KidTemplateIncompleteEntry}; used by the editor when appending blocks */
export type KidTemplateIncompleteDocumentTemplate =
  KidTemplateIncompleteEntryTemplate;

/**
 * Payload model for template updates coming from the KID editor.
 * Mirrors backend `KidTemplateContentChangedModel`.
 */
export type KidTemplateContentChangedModel = {
  template_id: string;
  updated_template_content?: string | null;
  existing_markers: KidMarker[];
};

export type SaveKidDocumentPayload = KidDocumentIdentifierPayload & {
  updated_kid_data_models: UpdatedKidDataModel[];
  updated_kid_templates?: KidTemplateContentChangedModel[];
  /**
   * Optional template insert/remove operations performed as part of the update.
   * Backend `UpdateKidTemplatesPayload`.
   */
  inserted_kid_ids?: string[];
  removed_kid_ids?: string[];
};

export type ReviewKidDocumentPayload = KidDocumentIdentifierPayload & {
  status_change_to: KidStatus.REVIEWED | KidStatus.REJECTED;
  message?: string;
};

export type UpdateOverviewStatusPayload = KidDocumentIdentifierPayload & {
  status_change_to: KidStatus.EDITED | KidStatus.EDITING;
};

export type KidMarker = {
  marker_type: string;
  placeholder: string;
  value: string;
  label: string;
};

/** Shared fields for kid data record (create/update vs API response) */
type KidDataRecordBase = {
  kid_create_year: number;
  client_id: string;
  fund_id: string;
  share_class_id: string;
  kid_id: string;
  kid_index: number;
  kid_id_create_date: string;
  field_id: string;
  existing_markers: KidMarker[];
  last_updating_timestamp: string;
  last_updated_by: string;
  tag: string;
};

export type UpdatedKidDataModel = KidDataRecordBase & {
  template_id?: string;
  content?: string;
  rendered_content?: string;
  format_options?: Record<string, any>;
};

// `manage_templates` endpoint removed; use `PUT /v1/kid/data/update` instead.

export type KidDataTemplateModel = {
  client_id: string;
  template_type: KidTemplateType;
  template_id: string;
  template_content: string;
  template_name: string;
  template_updating_datetime: string;
  document_language: string;
  template_is_active: boolean;
  reference_id: string | null;
  template_group_id: string | null;
  template_setup_status: KidTemplateSetupStatus | string;
  template_last_updated_by: string;
  template_rendered_content: string | null;
  existing_markers: KidMarker[] | null;
  template_last_updated_by_name: string | null;
};

export type KidDataResponse = KidDataRecordBase & {
  template_id: string;
  content: string;
  rendered_content: string;
  format_options: { font_size: number; [key: string]: any };
  layout: { fixed_width: number[]; margin: boolean };
};

export type KidLayoutLevel = 'fund_manager' | 'client' | 'sub_client';

export type KidLayoutStyleSummary = {
  client_id: string;
  kid_layout_level: KidLayoutLevel;
  kid_layout_value: string;
  document_language: string;
  kid_layout_id: string;
  kid_layout_name: string;
  kid_layout_is_active: boolean;
  kid_layout_create_date: string;
  last_updating_timestamp: string;
  last_updated_by: string;
};

export type KidLayoutTemplateModel = {
  kid_layout_last_updated_year: number;
  kid_layout_id: string;
  kid_index: number;
  kid_layout_level: KidLayoutLevel;
  kid_layout_value: string;
  client_id: string;
  template_id: string | null;
  tag: string;
  content: string | null;
  format_options: Record<string, any> | null;
  last_updating_timestamp: string;
  last_updated_by: string;
  field_id: string;
  layout?: Record<string, any> | null;
  kid_layout_name: string;
  document_language: string;
  existing_markers: KidMarker[];
  rendered_content: string | null;
};

/**
 * KID Figures Model - raw KID figures data.
 * Mirrors backend KidFiguresModel; excludes rendered_content.
 */
export interface KidFiguresModel {
  riy: [number, number][];
  currency: string;
  frequency: string;
  stats2: [string, number][];
  scenario_dictionary: {
    favourable: Record<string, [number, number]>;
    moderate: Record<string, [number, number]>;
    unfavourable: Record<string, [number, number]>;
    stressed: Record<string, [number, number]>;
  };
  CRM: number;
  stats: [string, number][];
  scenarios: [string, [number, number][]][];
  riy_dict: Record<string, [number, number]>;
  average_moderate_returns: number;
  VEV: number;
  end_date: string;
  used_scenarios: string[];
  SRI: number;
  MRM: number;
  stressed_volatility2: [number, number];
  investment_amount: number;
  share_class_name: string;
  name: string;
  holding_period: number;
  costs: number[];
  stressed_volatility: [number, number];
  isin: string;
  base_currency: string;
  principal_performance_fee: number | null;
  fund_id: string;
  calculation_date: string;
  is_published: boolean;
}

export interface KidFiguresScenarioPeriodValue {
  value: number;
  return_value: number;
}

export interface KidFiguresScenarioPeriods {
  '1yr_period': KidFiguresScenarioPeriodValue;
  holding_period: KidFiguresScenarioPeriodValue;
}

export interface KidFiguresRecordModel {
  calculation_year: number;
  client_id: string;
  fund_id: string;
  share_class_id: string;
  calculation_date: string;
  data: {
    costs: {
      entry_cost: number;
      exit_cost: number;
      ongoing_cost: number;
      transaction_cost: number;
      applied_performance_fee: number;
      notional_performance_fee: number;
      has_performance_fee: boolean;
    };
    riy_data: {
      '1yr_period': {
        total_cost: number;
        riy: number;
      };
      holding_period: {
        total_cost: number;
        riy: number;
      };
    };
    scenario_data: {
      favourable: KidFiguresScenarioPeriods;
      moderate: KidFiguresScenarioPeriods;
      stressed: KidFiguresScenarioPeriods;
      unfavourable: KidFiguresScenarioPeriods;
    };
    sri: number;
  };
  last_updating_datetime: string;
}

export type CreateKidDocumentInput = {
  clientId: string;
  fundId: string;
  fundName: string;
  shareClassId: string;
  documentType: string;
  language: string;
  transactionCost: number;
  ongoingCharges: number;
  entryFee: number;
  exitFee: number;
  template_fund_id?: string;
  template_share_class_id?: string;
  template_share_class_name?: string;
  layout_style_id?: string;
  layout_last_updated_timestamp?: string;
};

export type CreateKidLayoutTemplatePayload = {
  client_id: string;
  kid_layout_level: KidLayoutLevel;
  kid_layout_value: string;
  document_language: string;
  kid_layout_id: string;
  kid_layout_name: string;
  kid_layout_models: KidLayoutTemplateModel[];
};

export enum KidTemplateType {
  MANCO = 'manco',
  FUND_MANAGER = 'fund_manager',
  SUB_FUND = 'sub_fund',
  CIU_FUND = 'ciu_fund',
  ADMINISTRATOR = 'administrator',
}

export const KID_TEMPLATE_TYPE_RANK: Record<KidTemplateType, number> = {
  [KidTemplateType.MANCO]: 50,
  [KidTemplateType.ADMINISTRATOR]: 40,
  [KidTemplateType.CIU_FUND]: 30,
  [KidTemplateType.FUND_MANAGER]: 20,
  [KidTemplateType.SUB_FUND]: 0,
};

export const getKidTemplateTypeRank = (templateType: KidTemplateType): number =>
  KID_TEMPLATE_TYPE_RANK[templateType];

export type KidTemplateModel = {
  template_id: string;
  client_id: string;
  template_name: string;
  template_type: KidTemplateType;
  template_content: string;
  document_language: string;
  template_group_id?: string | null;
  reference_id?: string | null;
  format_options?: Record<string, unknown> | null;
  last_updating_datetime?: string;
  last_updated_by?: string;
  template_is_active?: boolean;
  [key: string]: unknown;
};

export type NecessaryKidTemplatesFundEntry = {
  fund_name: string;
  fund_name_full: string;
  templates: KidTemplateModel[];
};

export type NecessaryKidTemplatesResponse = Record<
  string,
  NecessaryKidTemplatesFundEntry
>;

export type KidTemplateOutputModel = KidTemplateModel & {
  rendered_content?: string | null;
};

export type CreateKidTemplatePayload = {
  template_name: string;
  template_type: KidTemplateType;
  template_content: string;
  reference_id?: string | null;
  template_group_id?: string | null;
  document_language?: string;
  format_options?: Record<string, unknown> | null;
};

export type UpdateKidTemplateContentPayload = {
  updated_content: string;
  format_options?: Record<string, unknown> | null;
};

export type UpdateKidTemplateNamePayload = {
  template_name: string;
};

export type DowngradeKidTemplatePayload = {
  updated_template_type: KidTemplateType;
  updated_reference_id?: string | null;
};

export type AddTemplateToDocumentPayload = {
  fund_id: string;
  share_class_id: string;
  template_id: string;
  document_language: string;
  document_index: number;
  kid_tag: string;
  format_options?: Record<string, unknown> | null;
  kid_doc_layout?: Record<string, unknown> | null;
};

export type RemoveTemplateFromDocumentPayload = {
  fund_id: string;
  share_class_id: string;
  template_id: string;
  document_language: string;
};

export type KidTemplateSetupStatusResponse = {
  overviews_with_invalid_template: KidOverviewDataItem[];
  overviews_with_missing_template: KidOverviewDataItem[];
  template: KidTemplateModel;
};
